import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import MyCart from './MyCart';
import { CartContext } from '../../context/CartContext';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  Autocomplete,
  Chip,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../adapters/api';
import Swal from 'sweetalert2';

const priceLevelMap = { 0: "สด", 1: "7%", 2: "10%", 3: "12%", 4: "15%" };

const DEFAULT_CUST_CODE = "AR-0001";
const DEFAULT_CUST_NAME = "ขายสด";

function CartModal({ show, handleClose }) {
  const {
    cart, sumCart, cartGuid, deleteCart, currentOrderNo,
    setPriceLevel, fetchCart, fetchSumCart,
    setCustomerId: setCtxCustomerId, setCustomerName: setCtxCustomerName,
    lockInfo, setLockInfo, resetCartState,
  } = useContext(CartContext);
  const { currentUser } = useContext(AuthContext);
  // เลือกระดับราคาเองได้เฉพาะ admin (level >= 2) หรือแผนกขายเครดิต / แผนกธุรการเซลล์
  const canChangePriceLevel = (currentUser?.user_level ?? 0) >= 2 || (currentUser?.dept?.some(d => ['แผนกขายเครดิต', 'แผนกธุรการเซลล์'].includes(d)) ?? false);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [customers, setCustomers] = useState([]);
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(DEFAULT_CUST_NAME);
  const [customerId, setCustomerId] = useState(DEFAULT_CUST_CODE);
  const [inputValue, setInputValue] = useState(DEFAULT_CUST_NAME);
  const [priceType, setPriceType] = useState("สด");

  const totalWeight = cart.reduce(
    (acc, item) => acc + (Number(item.weight) || 0) * (Number(item.qty) || 0),
    0
  );

  const [appliedPromos, setAppliedPromos] = useState([]);

  useEffect(() => {
    if (!cartGuid) return;
    api.post('/api/promotions/evaluate', { cart_guid: cartGuid }, { withCredentials: true })
      .then(({ data }) => setAppliedPromos(data.applied || []))
      .catch(() => {});
  }, [cart, cartGuid]);

  const promosByItemCode = useMemo(() => {
    const map = {};
    for (const p of appliedPromos) {
      if (!p.matched_item_codes) continue;
      for (const entry of p.matched_item_codes) {
        const code = entry.item_code;
        if (!map[code]) map[code] = { promos: [], totalLineDiscount: 0 };
        map[code].promos.push(p);
        map[code].totalLineDiscount += entry.line_discount || 0;
      }
    }
    return map;
  }, [appliedPromos]);

  // Use each promo's own discount_amount (authoritative) rather than
  // reconstructing it from matched_item_codes — some rule types (e.g.
  // min_order_amount, customer_specific, tiered, bundle) never populate
  // matched_item_codes even though they still discount the cart total.
  const totalPromoDiscount = appliedPromos
    .filter(p => !p.is_free_gift)
    .reduce((sum, p) => sum + Number(p.discount_amount || 0), 0);

  const cartSubtotal = cart.reduce(
    (sum, item) => sum + Number(item.qty) * Number(item.price), 0
  );

  const discountedTotal = Math.max(0, cartSubtotal - totalPromoDiscount);

  // ---------- lock/unlock ----------

  const unlockBill = async () => {
    if (!lockInfo) return;
    try {
      await api.post('/api/unlockBill', { doc_no: lockInfo.doc_no, guid: lockInfo.guid });
    } catch (err) {
      console.error('unlockBill error:', err);
    }
    setLockInfo(null);
  };

  // ---------- helpers ----------

  const updateCartHeaderField = async (fieldName, fieldValue) => {
    try {
      await api.post("/api/updateCartHeaderField", {
        fieldName,
        fieldValue,
        creator_code: currentUser?.user_code,
      });
    } catch (err) {
      console.error("Error updating cart header field:", err);
    }
  };

  const updateCartPrices = async (level) => {
    try {
      await api.post("/api/updateCartPriceByLevel", { price_level: level });
      fetchCart();
      fetchSumCart();
    } catch (err) {
      console.error("Error updating cart prices:", err);
    }
  };

  const handleSalePersonChange = (eOrValue) => {
    const inputVal = eOrValue?.target ? eOrValue.target.value : eOrValue;
    updateCartHeaderField("sale_code", inputVal);
    const selectedSale = salespersons.find((s) => s.code === inputVal);
    if (selectedSale) {
      updateCartHeaderField("branch_code", selectedSale.branch_code);
      updateCartHeaderField("pickup_branch", selectedSale.branch_code);
    }
  };

  const handleArTypeChange = (eOrValue) => {
    const inputVal = eOrValue?.target ? eOrValue.target.value : eOrValue;
    if (inputVal == null) {
      updateCartHeaderField("inquiry_type", null);
    } else {
      updateCartHeaderField("inquiry_type", inputVal === "เครดิต" ? 0 : 1);
    }
  };

  const handlePriceTypeChange = (eOrValue) => {
    const selectedPriceText = eOrValue?.target ? eOrValue.target.value : eOrValue;
    if (selectedPriceText == null) {
      setPriceType(null);
      setPriceLevel(0);
      updateCartPrices(null);
      updateCartHeaderField("price_level", null);
    } else {
      const selectedPriceLevel =
        Object.keys(priceLevelMap).find((key) => priceLevelMap[key] === selectedPriceText) || 0;
      setPriceType(selectedPriceText);
      setPriceLevel(Number(selectedPriceLevel));
      updateCartPrices(selectedPriceLevel);
      updateCartHeaderField("price_level", selectedPriceLevel);
    }
  };

  const handleTaxTypeChange = (eOrValue) => {
    const inputVal = eOrValue?.target ? eOrValue.target.value : eOrValue;
    const newValue = inputVal === "vat" ? "" : inputVal;
    updateCartHeaderField("set_tax_type", newValue);
  };

  const handleShippingTypeChange = (eOrValue) => {
    const inputVal = eOrValue?.target ? eOrValue.target.value : eOrValue;
    if (inputVal != null) {
      updateCartHeaderField("send_type", inputVal === "จัดส่ง" ? 1 : 0);
    }
  };

  const handleCreditDayChange = (eOrValue) => {
    const inputVal = eOrValue?.target ? eOrValue.target.value : eOrValue;
    const value = parseInt(inputVal, 10);
    updateCartHeaderField("credit_day", !isNaN(value) ? value : 0);
  };

  const handleCustomerChange = async (event, value) => {
    if (value) {
      setInputValue(value.name_1 || "");
      setCustomer(value.name_1);
      setCustomerId(value.code);
      setCtxCustomerId(value.code);
      setCtxCustomerName(value.name_1);
      // ต้องบันทึก cust_code ก่อนตั้งระดับราคา — backend ตรวจว่าตรงกับระดับราคาของลูกค้าในตะกร้า
      await updateCartHeaderField("cust_code", value.code);
      handleSalePersonChange(value.sale_code);
      handleArTypeChange(value.sale_type);
      handlePriceTypeChange(priceLevelMap[value.price_level]);
      handleTaxTypeChange(value.set_tax_type);
      handleShippingTypeChange(value.sale_type === "เครดิต" ? "จัดส่ง" : "รับเอง");
      handleCreditDayChange(value.credit_day);
      updateCartHeaderField("delivery_location_id", null);
      updateCartHeaderField("shipping_cost", 0);
    } else {
      setInputValue("");
      setCustomer("");
      setCustomerId("");
      setCtxCustomerId(null);
      setCtxCustomerName(null);
      setPriceType("สด");
    }
  };

  // ค้นหาลูกค้าจากชื่อหรือเบอร์โทร — debounced 300 ms, ยกเลิก request เก่าที่ค้างอยู่
  // (กันผลของคำค้นเก่ามาทับผลของคำค้นล่าสุด)
  const handleSearch = (event, value) => {
    if (searchAbortRef.current) searchAbortRef.current.abort();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!value || value.length < 2) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setLoading(true);
      try {
        const response = await api.post(
          `/api/customerList?search=${encodeURIComponent(value)}`,
          null,
          { signal: controller.signal }
        );
        setCustomers(response.data);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error("Error fetching customer list:", err);
        }
      } finally {
        if (searchAbortRef.current === controller) setLoading(false);
      }
    }, 300);
  };

  // ---------- default customer ----------

  const applyDefaultCustomer = async (salespersonsList) => {
    try {
      const res = await api.post(`/api/customerList?search=${DEFAULT_CUST_NAME}`);
      const defaultCust = res.data.find((c) => c.code === DEFAULT_CUST_CODE);
      if (defaultCust) {
        setInputValue(defaultCust.name_1);
        setCustomer(defaultCust.name_1);
        setCustomerId(defaultCust.code);
        setCtxCustomerId(defaultCust.code);
        setCtxCustomerName(defaultCust.name_1);
        await updateCartHeaderField("cust_code", defaultCust.code);
        const sp = (salespersonsList || salespersons).find((s) => s.code === defaultCust.sale_code);
        updateCartHeaderField("sale_code", defaultCust.sale_code);
        if (sp) {
          updateCartHeaderField("branch_code", sp.branch_code);
          updateCartHeaderField("pickup_branch", sp.branch_code);
        }
        handleArTypeChange(defaultCust.sale_type);
        handlePriceTypeChange(priceLevelMap[defaultCust.price_level ?? 0]);
        handleTaxTypeChange(defaultCust.set_tax_type);
        handleShippingTypeChange(defaultCust.sale_type === "เครดิต" ? "จัดส่ง" : "รับเอง");
        handleCreditDayChange(defaultCust.credit_day);
        updateCartHeaderField("delivery_location_id", null);
        updateCartHeaderField("shipping_cost", 0);
      }
    } catch (err) {
      console.error("Error applying default customer:", err);
    }
  };

  // ---------- init ----------

  useEffect(() => {
    if (!show) return;
    const init = async () => {
      try {
        const [salesRes, headerRes] = await Promise.all([
          api.post("/api/getSalespersons", {}, { withCredentials: true }),
          api.post("/api/getDefaultCartHeader", { user: currentUser?.user_code }, { withCredentials: true }),
        ]);
        setSalespersons(salesRes.data);
        const header = headerRes.data;
        if (header?.cust_code) {
          setCustomerId(header.cust_code);
          setCustomer(header.cust_name || "");
          setInputValue(header.cust_name || "");
          setPriceType(priceLevelMap[header.price_level] || "สด");
          setCtxCustomerId(header.cust_code);
          setCtxCustomerName(header.cust_name || "");
        } else {
          await applyDefaultCustomer(salesRes.data);
        }
      } catch (err) {
        console.error("Error initialising CartModal:", err);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ---------- clear cart ----------

  const handleClearDetails = () => {
    Swal.fire({
      title: "ยืนยันยกเลิกการแก้ไข",
      text: "บิลที่กำลังแก้ไขอยู่จะไม่ถูกบันทึกการแก้ไข",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#757575",
      confirmButtonText: "ยืนยันยกเลิก",
      cancelButtonText: "ยกเลิก",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await unlockBill();
        await deleteCart();
        resetCartState();
        await applyDefaultCustomer();
        Swal.fire({
          title: "เรียบร้อย",
          text: "ข้อมูลในตะกร้าถูกล้างเรียบร้อยแล้ว",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          handleClose();
        });
      }
    });
  };

  // ---------- render ----------

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen={isMobileScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobileScreen ? 0 : 2,
          ...(isMobileScreen
            ? { m: 0, width: '100%', maxWidth: '100%', height: '100%', maxHeight: '100%' }
            : { maxHeight: '85vh' }),
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.3 }}>
              รายการสินค้า
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {currentOrderNo ? `แก้ไขบิล: ${currentOrderNo}` : 'สร้างบิลใหม่'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Customer + price — fixed */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ flex: '0 0 70%' }}>
              <Autocomplete
                freeSolo={false}
                options={customers.filter(Boolean)}
                value={
                  customer
                    ? customers.find((c) => c.code === customerId) || { code: customerId, name_1: customer }
                    : null
                }
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason !== 'reset') {
                    setInputValue(newInputValue);
                    handleSearch(event, newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleCustomerChange(event, newValue);
                  } else {
                    setInputValue("");
                    setCustomer("");
                    setCustomerId("");
                  }
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : (option?.name_1 ?? '')
                }
                isOptionEqualToValue={(option, value) => option?.code === value?.code}
                filterOptions={(options, { inputValue }) => {
                  const q = (inputValue ?? '').toLowerCase();
                  const digits = q.replace(/\D/g, '');
                  return options.filter((o) =>
                    o?.is_fuzzy ||
                    o?.name_1?.toLowerCase().includes(q) ||
                    (digits.length >= 3 && (o?.telephone ?? '').replace(/\D/g, '').includes(digits))
                  );
                }}
                loading={loading}
                renderOption={(props, option) => (
                  <li {...props} key={option?.code ?? props.key}>
                    <Typography component="span" sx={{ color: 'grey.500', fontWeight: 300, fontSize: '0.75rem' }}>
                      {option?.code ?? ''}
                    </Typography>
                    <Typography component="span" sx={{ ml: 1 }}>
                      {option?.name_1 ?? ''}
                    </Typography>
                    {option?.telephone && (
                      <Typography component="span" sx={{ ml: 1, color: 'grey.500', fontSize: '0.75rem' }}>
                        ({option.telephone})
                      </Typography>
                    )}
                    {option?.is_fuzzy && (
                      <Chip label="ใกล้เคียง" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="ค้นหาลูกค้า"
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{ ...params.inputProps, style: { ...params.inputProps?.style, fontSize: '15px' } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            <Select
              size="small"
              value={priceType || "สด"}
              onChange={handlePriceTypeChange}
              disabled={!canChangePriceLevel}
              sx={{ flex: '1 1 0', fontSize: '15px' }}
            >
              <MenuItem value="สด">สด</MenuItem>
              <MenuItem value="7%">7%</MenuItem>
              <MenuItem value="10%">10%</MenuItem>
              <MenuItem value="12%">12%</MenuItem>
              <MenuItem value="15%">15%</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Cart items — scrollable */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {cart.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', fontSize: '14px' }}>
              ไม่มีสินค้าในตะกร้า
            </Box>
          ) : (
            <MyCart promosByItemCode={promosByItemCode} />
          )}
        </Box>

        {/* Summary — fixed */}
        <Box sx={{
          bgcolor: 'action.hover',
          flexShrink: 0,
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          display: 'flex',
          // มือถือ: น้ำหนัก / โปรโมชัน / ยอดรวม ไม่พอในบรรทัดเดียว → ขึ้นบรรทัดใหม่แทนล้นขอบจอ
          flexWrap: 'wrap',
          rowGap: 0.5,
          columnGap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
            น้ำหนักรวม: {totalWeight.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} กก.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
            {appliedPromos.map((p, i) => {
              const totalDiscount = (p.matched_item_codes || []).reduce((s, e) => s + (e.line_discount || 0), 0);
              return (
                <Box key={i} display="flex" alignItems="center" gap={0.3}>
                  <LocalOfferIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
                  <Typography sx={{ fontSize: 12, color: '#2e7d32', lineHeight: 1 }}>
                    {p.promotion_name} (-{totalDiscount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บ.)
                  </Typography>
                </Box>
              );
            })}
            <Typography sx={{ fontSize: '14px', color: 'text.secondary', ml: appliedPromos.length ? 1 : 0 }}>รวมทั้งสิ้น</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 500, color: '#e90808' }}>
              {discountedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>บาท</Typography>
          </Box>
        </Box>

        {/* Footer buttons — fixed */}
        <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, justifyContent: 'flex-end', flexShrink: 0, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearDetails}
            disabled={cart.length === 0}
            sx={{ minWidth: { xs: 0, sm: 100 }, flex: { xs: '1 1 0', sm: '0 0 auto' }, whiteSpace: 'nowrap' }}
          >
            ล้างข้อมูล
          </Button>
          <Button
            variant="outlined"
            onClick={() => { handleClose(); navigate('/'); }}
            sx={{ minWidth: { xs: 0, sm: 130 }, flex: { xs: '1 1 0', sm: '0 0 auto' }, whiteSpace: 'nowrap' }}
          >
            เลือกสินค้าเพิ่ม
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={cart.length === 0}
            onClick={() => { handleClose(); navigate('/checkout'); }}
            sx={{ minWidth: { xs: 0, sm: 100 }, flex: { xs: '1 1 0', sm: '0 0 auto' }, whiteSpace: 'nowrap' }}
          >
            ออกบิล
          </Button>
        </Box>

      </Box>
    </Dialog>
  );
}

export default CartModal;
